import {useState} from 'react';
import {usePlants} from '../api/posts';
import EmptyState from '../components/feedback/EmptyState';
import ErrorBox from '../components/feedback/ErrorBox';
import {SkeletonList} from '../components/feedback/Skeleton';
import Icon from '../components/Icon';
import PlantRow, {SpeciesRow} from '../components/plant/PlantRow';
import {useMe} from '../hooks/useAuth';
import {groupSpecies, matchesPlant, plantSubtitle, Species} from '../lib/plants';
import {PlantGroup} from '../types';

// The plant-centred way in (wireframe "plants"): everything the gardener has ever named, one
// row per species ("Garlic · 11 varieties" opens the species page; a name with one variety is
// just its plant), in three bands, with a search that finds any variety. Built entirely from
// tells; nothing to maintain here.

const recency = (s: Species) => s.lead.latest?.whenLocal ?? s.lastAt ?? '';

const BANDS: {group: PlantGroup; label: string; sort: (a: Species, b: Species) => number}[] = [
    {group: 'growing', label: 'Growing now', sort: (a, b) => (recency(a) < recency(b) ? 1 : -1)},
    {group: 'permanent', label: 'Permanent', sort: (a, b) => a.name.localeCompare(b.name)},
    {group: 'past', label: 'Past seasons', sort: (a, b) => ((a.lastAt ?? '') < (b.lastAt ?? '') ? 1 : -1)}
];

export default function PlantsPage() {
    const me = useMe();
    const plants = usePlants();
    const [q, setQ] = useState('');
    const hemisphere = me.data?.hemisphere;
    const all = plants.data ?? [];
    const species = groupSpecies(all);
    const growing = species.filter(s => s.group === 'growing').length;
    const matches = q.trim() ? all.filter(p => matchesPlant(p, q)).sort((a, b) => a.label.localeCompare(b.label)) : null;

    const row = (s: Species) => {
        if (s.plants.length === 1) return <PlantRow key={s.key} plant={s.plants[0]} subtitle={plantSubtitle(s.plants[0], hemisphere)} />;
        const lead = plantSubtitle(s.lead, hemisphere);
        return <SpeciesRow key={s.key} species={s} subtitle={[s.lead.variety, lead].filter(Boolean).join(': ')} />;
    };

    return (
        <div className="settle-in flex flex-col gap-5">
            <header className="pt-2">
                <h1 className="display m-0">Plants</h1>
                {plants.data && all.length > 0 && (
                    <p className="meta mt-1">
                        {species.length} {species.length === 1 ? 'plant' : 'plants'}
                        {all.length > species.length ? ` (${all.length} with their varieties)` : ''}
                        {growing > 0 ? ` · ${growing} growing now` : ''}
                    </p>
                )}
            </header>

            {all.length > 0 && (
                <label className="field flex items-center gap-3">
                    <Icon name="leaf" size={20} className="shrink-0 text-shoot-text" />
                    <input type="search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search plants and varieties…" aria-label="Search plants" className="min-w-0 flex-1 bg-transparent outline-none" />
                </label>
            )}

            {plants.isPending && <SkeletonList rows={6} />}
            {plants.isError && <ErrorBox error={plants.error} onRetry={() => void plants.refetch()} />}

            {plants.isSuccess && all.length === 0 && (
                <EmptyState icon="leaf" title="No plants yet.">
                    Name a plant on a tell and it appears here, with every season it has had.
                </EmptyState>
            )}

            {matches && (
                matches.length > 0 ? (
                    <div className="rows rounded-[18px] border-[1.5px] border-line bg-surface px-4">
                        {matches.map(p => (
                            <PlantRow key={p.id} plant={p} subtitle={plantSubtitle(p, hemisphere)} />
                        ))}
                    </div>
                ) : (
                    <EmptyState icon="leaf" tone="plain" title="Nothing matches that.">
                        Try fewer letters, or the plant&rsquo;s other name.
                    </EmptyState>
                )
            )}

            {!matches &&
                BANDS.map(band => {
                    const items = species.filter(s => s.group === band.group).sort(band.sort);
                    if (items.length === 0) return null;
                    return (
                        <section key={band.group} className="flex flex-col gap-2">
                            <h2 className="band-label m-0 text-ink-2">{band.label}</h2>
                            <div className="rows rounded-[18px] border-[1.5px] border-line bg-surface px-4">{items.map(row)}</div>
                        </section>
                    );
                })}
        </div>
    );
}
